import React from 'react';

import { Link, Box } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import DataGrid from '@gogame/data-grid';
import {
  useScreenDispatch,
  useScreenState,
  useScreenTitle,
} from '@gogame/screens';

import {
  BriefFormEdit,
  BriefFormDuplicate,
  BriefFormCreate,
} from 'scenes/Influencers/Briefs/components/BriefForm';
import {
  COLUMN_ID,
  COLUMN_NAME,
  COLUMN_APPLICATION,
  COLUMN_MASTER_CAMPAIGNS,
  COLUMN_LAST_UPDATED_BY,
  COLUMN_LAST_UPDATED_DATE,
  COLUMN_CREATED_BY,
  COLUMN_CREATED_DATE,
} from 'scenes/Influencers/Briefs/constants';
import { useLoadBriefs } from 'scenes/Influencers/Briefs/data';
import Toolbar from './Toolbar';
import {
  REORDER_COLUMNS,
  COLLAPSE_COLUMNS,
  SET_TABLE_SETTINGS,
} from 'scenes/Influencers/Briefs/screenReducer';
import MasterCampaignDetails from 'scenes/Influencers/MasterCampaigns/components/MasterCampaignDetails';

import BriefActions from '../Actions';

const BriefDataTable = () => {
  const [screenTitle] = useScreenTitle();
  const dispatch = useScreenDispatch();
  const screenState = useScreenState();
  const [childComponent, openChildComponent] = useChildComponent();

  const { filters, tableSettings, searchQuery, ready } = screenState;
  const { data, isLoading, isRefetching } = useLoadBriefs(
    {
      filters,
      tableSettings,
      searchQuery,
    },
    {
      enabled: ready,
    }
  );

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleEdit = ({ id }) => {
    openChildComponent(BriefFormEdit, { id });
  };

  const handleDuplicate = ({ id }) => {
    openChildComponent(BriefFormDuplicate, {
      id,
    });
  };

  const handleOpenMasterCampaign = (_e, { id }) => {
    openChildComponent(MasterCampaignDetails, { id });
  };

  const handleCreate = () => {
    openChildComponent(BriefFormCreate, {});
  };

  return (
    <React.Fragment>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading}
        fetching={isRefetching}
        ready={ready}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={<Toolbar onCreate={handleCreate} />}
        storageKey={'influencersBriefs'}
        defaultColumnWidth={120}
        filtered={!!screenState.searchQuery}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          width={90}
          fixed={true}
          resizable={false}
          align={'right'}
          headerAlign={'right'}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          type={'text'}
          headerName={'Name'}
          minWidth={210}
          flex={1}
          formatter={(value, row) => {
            return (
              <React.Fragment key={row.id}>
                <Link
                  href={row.attachmentUrl}
                  target={'_blank'}
                  sx={{ display: 'block' }}
                >
                  {value}
                </Link>
                <Box color={'text.secondary'} as={'span'}>
                  {row.description}
                </Box>
              </React.Fragment>
            );
          }}
        />
        <DataGrid.Column
          field={COLUMN_APPLICATION}
          type={'text'}
          headerName={'Application'}
          width={170}
        />
        <DataGrid.Column
          field={COLUMN_MASTER_CAMPAIGNS}
          headerName={'Master Campaigns'}
          limit={1}
          type={'list'}
          sortable={false}
          minWidth={170}
          getItemValue={(v) => {
            return (
              <Link onClick={(e) => handleOpenMasterCampaign(e, v)}>
                {v.name}
              </Link>
            );
          }}
        />
        <DataGrid.Column
          field={COLUMN_LAST_UPDATED_BY}
          headerName={'Last updated by'}
          width={170}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_LAST_UPDATED_DATE}
          headerName={'Last updated'}
          width={135}
          type={'date'}
          template={'LLL'}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          headerName={'Created by'}
          width={170}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          headerName={'Created'}
          width={135}
          type={'date'}
          template={'LLL'}
        />

        <DataGrid.Column
          actionsColDef
          type={'actions'}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          limit={3}
          actionsComponent={BriefActions}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};

export default BriefDataTable;
